import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, Truck, Upload, X, Package, User, Lock, ShieldCheck, Check, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import ChangeEmailCard from "@/components/ChangeEmailCard";
import PasswordInput from "@/components/PasswordInput";
import AddressFields from "@/components/AddressFields";
import { useAuth } from "@/contexts/AuthContext";
import { cacheAdminDatePreference } from "@/lib/adminDateRange";

// Resize a logo to keep DB documents small; preserves aspect ratio.
// Max size (chars) for the logo data-URL stored as PNG before we fall back to JPEG.
const MAX_LOGO_PNG_CHARS = 300_000;
async function compressLogo(file) {
  if (/^image\/svg\+xml$/i.test(file.type)) {
    return await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  }
  const srcUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = srcUrl; });
  const maxEdge = 480;
  const ratio = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * ratio));
  const h = Math.max(1, Math.round(img.naturalHeight * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  // Try PNG first to keep transparency; fall back to JPEG if too big.
  const png = canvas.toDataURL("image/png");
  if (png.length < MAX_LOGO_PNG_CHARS) return png;
  return canvas.toDataURL("image/jpeg", 0.9);
}

function LogoUploader({ value, onChange }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//i.test(f.type)) {
      toast.error("Please choose an image file");
      e.target.value = ""; return;
    }
    setBusy(true);
    try {
      const dataUrl = await compressLogo(f);
      onChange(dataUrl);
      toast.success("Logo selected — click Save settings to apply");
    } catch (_) {
      toast.error("Couldn't process that image — try a different one");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-4 border border-border rounded-sm p-3 bg-secondary/30">
      <div className="w-20 h-20 flex items-center justify-center border border-border bg-background rounded-sm overflow-hidden flex-shrink-0">
        {value ? (
          <img src={value} alt="Business logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">No logo</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">Business logo</div>
        <div className="text-xs text-muted-foreground mb-2">Shown top-left of every invoice (screen, PDF, print). Auto-resized for crisp printing.</div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="logo-file-input" />
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="logo-upload-button">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Compressing…" : value ? "Replace" : "Upload"}
          </Button>
          {value && !busy && (
            <Button type="button" variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive" onClick={() => onChange("")} data-testid="logo-remove-button">
              <X className="w-3.5 h-3.5 mr-1.5" /> Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function UsersManager({ kind, title, description, Icon, endpoint }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const load = useCallback(() => api.get(endpoint).then((r) => setList(r.data)).catch(() => {}), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email, and password are required");
    try {
      await api.post(endpoint, form);
      toast.success(`${title.replace(/s$/, "")} account created`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${kind} ${d.name} (${d.email})?`)) return;
    try { await api.delete(`${endpoint}/${d.id}`); toast.success(`${title.replace(/s$/, "")} deleted`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="border border-border p-8 space-y-4" data-testid={`${kind}-manager`}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>

      <div className="border border-border rounded-sm p-3 grid grid-cols-12 gap-2 items-end bg-secondary/30">
        <div className="col-span-12 sm:col-span-3"><Label className="text-xs">Name</Label><Input className="rounded-sm" placeholder="e.g. Alex" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${kind}-form-name`} /></div>
        <div className="col-span-12 sm:col-span-4"><Label className="text-xs">Login email</Label><Input className="rounded-sm" type="email" placeholder="alex@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${kind}-form-email`} /></div>
        <div className="col-span-10 sm:col-span-4"><Label className="text-xs">Password</Label><PasswordInput className="rounded-sm" placeholder="min 4 chars" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} inputTestId={`${kind}-form-password`} testId={`${kind}-form-password-toggle`} /></div>
        <div className="col-span-2 sm:col-span-1"><Button onClick={add} className="rounded-sm w-full" data-testid={`${kind}-form-add`}><Plus className="w-4 h-4" /></Button></div>
      </div>

      {list.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No {title.toLowerCase()} yet.</p>}
      <div className="space-y-1">
        {list.map((d) => (
          <div key={d.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2" data-testid={`${kind}-row-${d.id}`}>
            <div className="flex-1">
              <div className="font-medium text-sm">{d.name}{d.is_self ? <span className="text-xs text-muted-foreground"> (you)</span> : null}</div>
              <div className="text-xs text-muted-foreground font-mono">{d.email}</div>
            </div>
            {!d.is_self && (
            <Button size="sm" variant="ghost" onClick={() => remove(d)} className="rounded-sm text-destructive hover:text-destructive" data-testid={`${kind}-delete-${d.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSecretary = user?.role === "secretary";
  const [s, setS] = useState(null);
  // Tracks the last server snapshot so onBlur auto-saves know whether anything
  // actually changed (avoids spurious PUTs while tabbing through read-only fields).
  const lastSavedRef = useRef("");
  const [saveState, setSaveState] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const savedTimerRef = useRef(null);

  const loadSettings = useCallback(async () => {
    const r = await api.get("/settings");
    setS(r.data);
    lastSavedRef.current = JSON.stringify(r.data);
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); }, []);

  const flashSaved = () => {
    setSaveState("saved");
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    // Keep the green "Saved" visible long enough for end users (and tests) to
    // see it after a quick blur-save.
    savedTimerRef.current = setTimeout(() => setSaveState("idle"), 2500);
  };

  // Called on every field blur — only PUTs when the JSON snapshot actually changed.
  const autoSave = useCallback(async (override) => {
    const next = override || s;
    if (!next) return;
    const snap = JSON.stringify(next);
    if (snap === lastSavedRef.current) return;
    setSaveState("saving");
    try {
      const r = await api.put("/settings", next);
      setS(r.data);
      lastSavedRef.current = JSON.stringify(r.data);
      flashSaved();
    } catch (e) {
      setSaveState("error");
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [s]);

  // The security toggle saves instantly — no need to find the Save button.
  const toggleLoginVerification = async (v) => {
    const next = { ...s, require_login_verification: v };
    setS(next);
    try {
      const r = await api.put("/settings", next);
      setS(r.data);
      toast.success(v ? "New-device email codes turned ON" : "New-device email codes turned OFF");
    } catch (e) {
      setS({ ...s, require_login_verification: !v });
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const toggleCalendarYearDefault = async (v) => {
    const next = { ...s, default_calendar_year_filter: v };
    setS(next);
    try {
      const r = await api.put("/settings", next);
      setS(r.data);
      cacheAdminDatePreference(v);
      lastSavedRef.current = JSON.stringify(r.data);
      toast.success(v ? "Admin lists now open to the current calendar year" : "Admin lists now open to all history");
    } catch (e) {
      setS({ ...s, default_calendar_year_filter: !v });
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  if (!s) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-6 animate-fade-up max-w-2xl">
      <div>
        <p className="overline">Configuration</p>
        <div className="flex items-center justify-between gap-4 mt-1">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">Settings</h1>
          <span
            className={`text-xs font-mono uppercase tracking-wider transition-opacity min-w-[80px] text-right ${
              saveState === "saved" ? "text-success opacity-100" :
              saveState === "saving" ? "text-muted-foreground opacity-100" :
              saveState === "error" ? "text-destructive opacity-100" : "opacity-0"
            }`}
            aria-live="polite"
            data-testid="settings-save-status"
          >
            {saveState === "saved" && <span data-testid="settings-saved-indicator"><Check className="w-3 h-3 inline mr-1" />Saved</span>}
            {saveState === "saving" && <span data-testid="settings-saving-indicator">Saving…</span>}
            {saveState === "error" && <span data-testid="settings-save-error">Save failed</span>}
          </span>
        </div>
      </div>

      {isAdmin && (
        <div className="border border-border p-8 space-y-4" data-testid="date-display-settings">
          <div className="flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-primary" />
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Default date display</h2>
              <p className="text-xs text-muted-foreground">Shared by Catskills Pool and Management USA NJ admin screens.</p>
            </div>
          </div>
          <label className="flex items-start justify-between gap-4 border border-border rounded-sm p-4 cursor-pointer" data-testid="date-display-current-year-label">
            <div>
              <div className="font-medium text-sm">Open lists to the current calendar year</div>
              <p className="text-xs text-muted-foreground mt-1">On: January 1 through December 31 of this year. Off: all history. You can still change dates on each screen.</p>
            </div>
            <Switch checked={s.default_calendar_year_filter !== false} onCheckedChange={toggleCalendarYearDefault} data-testid="date-display-current-year-toggle" />
          </label>
        </div>
      )}

      <div className="border border-border p-8 space-y-4">
        <div>
          <Label className="flex items-center gap-1">Business name {isSecretary && <Lock className="w-3 h-3 text-muted-foreground" />}</Label>
          <Input className="rounded-sm" value={s.business_name}
                 onChange={(e) => setS({ ...s, business_name: e.target.value })}
                 onBlur={() => autoSave()}
                 readOnly={isSecretary} disabled={isSecretary}
                 data-testid="settings-business-name" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="flex items-center gap-1">Price per gallon {isSecretary && <Lock className="w-3 h-3 text-muted-foreground" />}</Label>
            <Input type="number" step="0.01" className="rounded-sm mono-num" value={s.price_per_gallon}
                   onChange={(e) => setS({ ...s, price_per_gallon: parseFloat(e.target.value || 0) })}
                   onBlur={() => autoSave()}
                   readOnly={isSecretary} disabled={isSecretary}
                   data-testid="settings-price-per-gallon" />
            {isSecretary && <p className="text-[10px] text-muted-foreground mt-1">Only the admin can change prices.</p>}
          </div>
          <div>
            <Label className="flex items-center gap-1">Default sales tax (%) {isSecretary && <Lock className="w-3 h-3 text-muted-foreground" />}</Label>
            <Input type="number" step="0.001" className="rounded-sm mono-num" value={s.default_tax_rate}
                   onChange={(e) => setS({ ...s, default_tax_rate: parseFloat(e.target.value || 0) })}
                   onBlur={() => autoSave()}
                   readOnly={isSecretary} disabled={isSecretary} />
          </div>
        </div>
        <div>
          <Label className="flex items-center gap-1">Payment currency {isSecretary && <Lock className="w-3 h-3 text-muted-foreground" />}</Label>
          {isSecretary ? (
            <Input value={s.currency || "USD"} readOnly disabled className="rounded-sm mono-num" />
          ) : (
            <Select value={s.currency || "USD"} onValueChange={(v) => {
              const next = { ...s, currency: v };
              setS(next);
              autoSave(next);
            }}>
              <SelectTrigger className="rounded-sm" data-testid="settings-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD — US Dollar</SelectItem>
                <SelectItem value="CAD">CAD — Canadian Dollar</SelectItem>
              </SelectContent>
            </Select>
          )}
          <p className="text-xs text-muted-foreground mt-1">Currency used on invoices and customer receipts.</p>
        </div>
        <div>
          <Label>Business address</Label>
          <div onBlur={() => autoSave()}>
            <AddressFields
              value={{
                address: s.business_address || "",
                city: s.business_city || "",
                state: s.business_state || "",
                zip_code: s.business_zip || "",
              }}
              onChange={(patch) => setS({
                ...s,
                business_address: patch.address ?? s.business_address,
                business_city: patch.city ?? s.business_city,
                business_state: patch.state ?? s.business_state,
                business_zip: patch.zip_code ?? s.business_zip,
              })}
              showLabels={false}
              testIdPrefix="settings-business-address"
            />
          </div>
        </div>
        <div><Label>Business phone</Label><Input className="rounded-sm" value={s.business_phone || ""} onChange={(e) => setS({ ...s, business_phone: e.target.value })} onBlur={() => autoSave()} /></div>
        <div className="pt-1">
          <LogoUploader value={s.logo_data_url || ""} onChange={(v) => { const next = { ...s, logo_data_url: v }; setS(next); autoSave(next); }} />
        </div>
        <p className="text-[11px] text-muted-foreground font-mono">
          Changes save automatically when you leave a field.
        </p>
      </div>

      <div className="border border-border p-8 space-y-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight">Chlorine inventory</h2>
            <p className="text-xs text-muted-foreground">Stock decrements automatically when tickets are closed. We&apos;ll flag the dashboard red when stock dips below the threshold.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Current stock (gallons)</Label>
            <Input type="number" step="0.1" className="rounded-sm mono-num" value={s.chlorine_stock_gallons ?? 0}
                   onChange={(e) => setS({ ...s, chlorine_stock_gallons: parseFloat(e.target.value || 0) })}
                   onBlur={() => autoSave()}
                   data-testid="settings-stock" />
          </div>
          <div>
            <Label>Low-stock alert threshold</Label>
            <Input type="number" step="0.1" className="rounded-sm mono-num" value={s.low_stock_threshold ?? 100}
                   onChange={(e) => setS({ ...s, low_stock_threshold: parseFloat(e.target.value || 0) })}
                   onBlur={() => autoSave()}
                   data-testid="settings-threshold" />
          </div>
        </div>
        <div>
          <Label>Driver average speed (mph)</Label>
          <Input type="number" step="1" min="5" max="80" className="rounded-sm mono-num w-32" value={s.driver_avg_mph ?? 30}
                 onChange={(e) => setS({ ...s, driver_avg_mph: parseFloat(e.target.value || 30) })}
                 onBlur={() => autoSave()}
                 data-testid="settings-mph" />
          <p className="text-xs text-muted-foreground mt-1">Used to estimate SMS arrival times sent to the next customer after each stop.</p>
        </div>
      </div>

      {isAdmin && (
        <div className="border border-border p-8 space-y-4" data-testid="security-settings">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Security</h2>
              <p className="text-xs text-muted-foreground">Extra protection for every account on this system.</p>
            </div>
          </div>
          <label className="flex items-start justify-between gap-4 border border-border rounded-sm p-4 cursor-pointer">
            <div>
              <div className="font-medium text-sm">Email code on new devices</div>
              <p className="text-xs text-muted-foreground mt-1">
                When someone signs in from a PC or phone we don&apos;t recognize, an 8-character code is emailed to them and must be
                entered to finish signing in. Each device only needs to verify once. Applies to admins, secretaries, drivers and customers.
              </p>
            </div>
            <Switch checked={!!s.require_login_verification}
                    onCheckedChange={toggleLoginVerification}
                    data-testid="settings-login-verification" />
          </label>
          <p className="text-[11px] text-muted-foreground font-mono">
            Saves instantly. Heads up: this sends an email per new device — it counts toward your Resend daily quota.
          </p>
        </div>
      )}

      {isAdmin && (
        <UsersManager
          kind="driver"
          title="Drivers"
          description="Drivers sign in at /login and land on /driver. They can confirm gallons and close tickets — never see invoice amounts or customer pricing."
          Icon={Truck}
          endpoint="/drivers"
        />
      )}

      {isAdmin && (
        <UsersManager
          kind="admin"
          title="Admins"
          description="Admins have FULL access to everything, including prices, users and settings. Only add people you fully trust."
          Icon={ShieldCheck}
          endpoint="/admins"
        />
      )}

      {isAdmin && (
        <UsersManager
          kind="secretary"
          title="Secretaries"
          description="Secretaries have full admin access EXCEPT: they cannot manage drivers/secretaries and cannot edit prices or tax rates."
          Icon={User}
          endpoint="/secretaries"
        />
      )}

      <ChangeEmailCard testId="admin-change-email" />
      <ChangePasswordCard testId="admin-change-password" />
    </div>
  );
}
